/* eslint-disable react/no-unused-state */
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'lodash';

import {
	Checkbox,
	TreeItem,
	List,
	TruncatedText,
	FlexItem,
	FlexLayout,
	listEventTypes,
} from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';

const { expandEventTypes } = listEventTypes;

class SparseTreeItem {
	_id: string;
	_getNumberOfChildren: (string) => number;
	_numberOfChildren: number = 0;
	_hasGetMoreLimit: boolean;

	_expanded: boolean = false;
	_position = null;

	parent: SparseTreeItem = null;
	children: SparseTreeItem[] = [];

	// For Get More...
	_limitNumberOfChildren = 0;
	constructor(id, getNumberOfChildren, hasGetMoreLimit) {
		this._id = id;
		this._getNumberOfChildren = getNumberOfChildren;
		this._numberOfChildren = 0;
		this._hasGetMoreLimit = hasGetMoreLimit;

		this._expanded = false;
		this._position = null;

		this.parent = null;
		this.children = [];

		// For Get More...
		this._limitNumberOfChildren = 0;
	}

	get id() {
		return this._id;
	}
	set numberOfChildren(numberOfChildren) {
		this._numberOfChildren = numberOfChildren;
	}
	get numberOfChildren() {
		if (this._numberOfChildren === 0) {
			const numberOfChildren = this._getNumberOfChildren(this._id);

			if (numberOfChildren > this._numberOfChildren) {
				this._numberOfChildren = numberOfChildren;
			}
		}

		if (this._hasGetMoreLimit) {
			return Math.min(this._limitNumberOfChildren + 1, this._numberOfChildren);
		}

		return this._numberOfChildren;
	}

	get level() {
		return this.parent ? this.parent.level + 1 : 0;
	}
	set expanded(expanded) {
		this._expanded = expanded;
	}
	get expanded() {
		return this._expanded;
	}
	get position() {
		return this._position;
	}
	set position(pos) {
		this._position = pos;
	}

	// Once clicked GetMore..., can call this function to increase _limitNumberOfChildren
	addLimitNumberOfChildren(cnt) {
		if (cnt <= 0) {
			throw new Error('addLimitNumberOfChildren parameter wrong.');
		}

		if (this._numberOfChildren === 0) {
			const numberOfChildren = this._getNumberOfChildren(this._id);

			if (numberOfChildren > this._numberOfChildren) {
				this._numberOfChildren = numberOfChildren;
			}
		}

		if (this._numberOfChildren === 0) {
			this._limitNumberOfChildren += cnt;
		} else {
			this._limitNumberOfChildren = Math.min(
				this._limitNumberOfChildren + cnt,
				this._numberOfChildren
			);
		}
	}

	// check if the child @position should be "Get More....";
	isGetMoreChildrenItem(position) {
		if (
			this._limitNumberOfChildren > 0 &&
			this.numberOfChildren === this._limitNumberOfChildren + 1
		) {
			return position === this._limitNumberOfChildren;
		}

		return false;
	}

	get length() {
		let len = 1;

		if (this.expanded) {
			len += this.numberOfChildren;

			this.children.forEach(child => {
				len += child.length - 1;
			});
		}

		return len;
	}

	getDistanceToRoot() {
		if (this.parent === null) {
			return -1;
		}

		let distance = this._position + this.parent.getDistanceToRoot() + 1;

		for (let idx = this.parent.children.length - 1; idx >= 0; --idx) {
			const sibling = this.parent.children[idx];
			// skip collapsed child.

			if (sibling.position < this._position && sibling.expanded !== false) {
				distance += sibling.length - 1;
			}
		}

		return distance;
	}

	getChild(position) {
		return this.children.find(ele => ele.position === position);
	}

	isChildExpanded(position) {
		const child = this.getChild(position);

		return child ? child.expanded : false;
	}

	// only expandable child would be added.
	addChild(position, id, hasChildren) {
		let child = null;

		if (hasChildren) {
			child = new SparseTreeItem(id, this._getNumberOfChildren, this._hasGetMoreLimit);
			child.parent = this;
			child.position = position;
			this.children = [...this.children, child]
				.sort((a: SparseTreeItem, b: SparseTreeItem): number => a.position - b.position)
				.slice();
		}

		return child;
	}

	// only expandable child would be added.
	purgeChild(position) {
		const index = this.children.findIndex(x => x.position === position);

		if (index >= 0) {
			this.children.splice(index, 1);
		}
	}

	walking(distance) {
		// walking itself.
		let remainDistance = distance - 1;

		if (remainDistance === 0 || this.expanded === false) {
			return { remainDistance, item: this.parent, position: this._position };
		}

		// walking inside.
		let walkingStop = 0;
		let position = 0;

		for (let idx = 0; idx < this.children.length; ++idx) {
			const child = this.children[idx];

			// skip collapsed child.
			if (child.expanded !== false) {
				// walking before child, not include child.
				if (remainDistance <= child.position - walkingStop) {
					position = walkingStop + remainDistance;
					position -= 1;

					return { remainDistance: 0, item: this, position };
				}

				remainDistance -= child.position - walkingStop;
				walkingStop = child.position;

				// walking child.
				const walked = child.walking(remainDistance);

				remainDistance = walked.remainDistance;

				if (remainDistance === 0) {
					return walked;
				}

				walkingStop = child.position + 1;
			}
		}

		position = Math.min(walkingStop + remainDistance, this.numberOfChildren) - 1;
		remainDistance -= position - walkingStop;
		remainDistance -= 1;

		return { remainDistance, item: this, position };
	}
}

class SparseTree {
	_limitChildrenCnt: number;
	_root: SparseTreeItem;

	constructor(rootId, getNumberOfChildren, limitChildrenCnt) {
		if (getNumberOfChildren) {
			this._limitChildrenCnt = Math.max(limitChildrenCnt, 0);
			this._root = new SparseTreeItem(
				rootId,
				getNumberOfChildren,
				this._limitChildrenCnt > 0
			);
			this._root.expanded = true;
			if (this._limitChildrenCnt > 0) {
				this._root.addLimitNumberOfChildren(this._limitChildrenCnt);
			}
		} else {
			this._root = null;
		}
	}

	get length() {
		return this._root ? this._root.length - 1 : 0;
	}
	get limitChildrenCnt() {
		return this._limitChildrenCnt;
	}

	locate(index) {
		if (!this._root) {
			throw new Error('Internal error in SparseTree.locate(), no root.');
		}

		const location = this._root.walking(index + 2);

		if (location.remainDistance > 0) {
			throw new Error(`Internal error in SparseTree.locate(${index}`);
		}

		return location;
	}

	expandCollaspe(index, type, id, hasChildren, onExpandCollapse) {
		const location = this.locate(index);

		let child = location.item.getChild(location.position);

		if (child) {
			if (child.id !== id) {
				throw new Error('Sparsetree data wrong.');
			}
		} else {
			child = location.item.addChild(location.position, id, hasChildren);
			if (this._limitChildrenCnt > 0) {
				child.addLimitNumberOfChildren(this._limitChildrenCnt);
			}
		}

		// ignore if it's not expandable
		if (!child) {
			return -1;
		}

		if (type === expandEventTypes.TOGGLE) {
			child.expanded = !child.expanded;
		} else if (type === expandEventTypes.EXPAND) {
			child.expanded = true;
		} else if (type === expandEventTypes.COLLAPSE) {
			child.expanded = false;
		}

		onExpandCollapse && onExpandCollapse(child.id, child.expanded);

		return this.length;
	}
}

export default class SelectHierarchyFilter extends Component<any, any> {
	static propTypes = {
		getValue: PropTypes.func.isRequired,
		getNumberOfChildren: PropTypes.func.isRequired,
		onExpandCollapse: PropTypes.func,
		onClick: PropTypes.func.isRequired,
		contentRenderer: PropTypes.func,
		limitChildrenCnt: PropTypes.number,
		TreeItemToUse: PropTypes.func,
		getTreeItemProps: PropTypes.func,
		selectionMode: PropTypes.oneOf(['none', 'single']).isRequired,
		readOnly: PropTypes.bool,
		ariaMultiselectable: PropTypes.bool,
	};

	static defaultProps = {
		TreeItemToUse: TreeItem,
		getTreeItemProps: x => x,
	};

	state = {
		length: 0,
	};

	componentDidMount() {
		this.initSpareTree();
	}

	componentWillUpdate() {
		// always re-fetch data value from parent control.
		this._itemMap = new Map();
	}

	// utility function used for dragDrop
	getFromMap = index => this._itemMap.get(index);

	_sparseTree = null;

	_list: List;

	_itemMap = new Map();

	initSpareTree() {
		const { limitChildrenCnt, getValue, getNumberOfChildren } = this.props;

		if (getValue) {
			const root = getValue(null);

			if (root.hasChildren) {
				this._sparseTree = new SparseTree(root.id, getNumberOfChildren, limitChildrenCnt);
				const { length } = this._sparseTree;

				this.setState({ length });
			}
		}
	}

	_refList = list => {
		this._list = list;
	};

	_contentRenderer = value => {
		const { contentRenderer, readOnly } = this.props;

		if (contentRenderer) {
			return contentRenderer(value);
		}

		return (
			<FlexLayout direction="row">
				<FlexItem>
					<FlexLayout>
						<FlexItem gutter={[0, 1, 0, 0]}>
							<Checkbox
								checked={value.selected}
								indeterminate={value.descendantsSelected}
								readOnly={readOnly}
								visual
							/>
						</FlexItem>
					</FlexLayout>
				</FlexItem>
				<FlexItem grow alignSelf="center">
					<TruncatedText value={value.caption ? value.caption : value.id} />
				</FlexItem>
			</FlexLayout>
		);
	};

	_getMoreRenderer = () => <span>{stringResource.get('loadMore')}</span>;

	_isExpanded = index => {
		const location = this._sparseTree.locate(index);

		return location.item.isChildExpanded(location.position);
	};

	_itemRenderer = (index, props) => {
		const location = this._sparseTree.locate(index);
		const { getTreeItemProps, getValue, TreeItemToUse } = this.props;
		const parentId = location.item.id;
		const { position } = location;
		const isGetMoreChildrenItem = location.item.isGetMoreChildrenItem(location.position);
		const value = isGetMoreChildrenItem
			? { parentId, position, isGetMoreChildrenItem }
			: getValue(parentId, position);
		const depth = location.item.level;
		const { hasChildren } = value;
		const pathToRoot = '';
		const ariaSelected = value && value.selected;

		this._itemMap.set(index, { ...value, depth });

		// in some case, parent control( i.e. mosejs ) do not know if node has children, always set hasChildren as true at beginning,
		// once user expand the node, eventually getNumberOfChildren() will be called, a node might be confirmed as leaf node,
		// if a node hasChildren changed to false, need to remove it from sparse tree.
		if (!hasChildren) {
			location.item.purgeChild(location.position);
		}

		const treeProps = getTreeItemProps({
			...props,
			depth,
			hasChildren,
			pathToRoot,
			index,
			ariaSelected,
		});

		let children;

		if (isGetMoreChildrenItem === true) {
			children = this._getMoreRenderer();
		} else {
			children = this._contentRenderer(this._itemMap.get(index));
		}

		let element;

		if (Array.isArray(children)) {
			// supress react's key warning: https://github.com/facebook/react/issues/
			element = React.createElement(TreeItemToUse, treeProps, ...children);
		} else {
			element = React.createElement(TreeItemToUse, treeProps, children);
		}

		return element;
	};

	_onExpandCollapse = event => {
		const { index, type } = event;

		if (index >= 0) {
			const { id, hasChildren } = this._itemMap.get(index);
			const length = this._sparseTree.expandCollaspe(
				index,
				type,
				id,
				hasChildren,
				this.props.onExpandCollapse
			);

			length && this.setState({ length });
		}
	};

	_onClick = event => {
		const { index } = event;
		const { onClick, readOnly } = this.props;

		if (readOnly) {
			return;
		}
		const location = this._sparseTree.locate(index);

		if (location.item.isGetMoreChildrenItem(location.position)) {
			location.item.addLimitNumberOfChildren(this._sparseTree.limitChildrenCnt);
			const { length } = this._sparseTree;

			this.setState({ length });
		} else if (this._itemMap.has(index)) {
			onClick && onClick(omit(this._itemMap.get(index), ['depth', 'hasChildren']));
		} else {
			throw new Error('onClick data is missing...');
		}
	};

	_getParentIndex = index => {
		const location = this._sparseTree.locate(index);

		if (location) {
			return location.item.getDistanceToRoot();
		}

		return -1;
	};

	render() {
		return (
			<List
				ref={this._refList}
				length={this._sparseTree ? this._sparseTree.length : 0}
				virtualized
				fullHeight
				itemRenderer={this._itemRenderer}
				getParentIndex={this._getParentIndex}
				selectionMode={this.props.selectionMode}
				listType="uniform"
				onExpand={this._onExpandCollapse}
				role="tree"
				isExpanded={this._isExpanded}
				onSelectionChange={this._onClick}
				getKey={v => v}
				hierarchical
				ariaMultiselectable={!!this.props.ariaMultiselectable}
			/>
		);
	}
}
